import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all category services
export const fetchCategoryServices = createAsyncThunk(
  "categoryServices/fetchCategoryServices",
  async ({ page = 1, per_page = 15, status, search, category_uuid } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    if (category_uuid) params.append("category_uuid", category_uuid);
    
    const res = await http.get(`/dashboard/category-services?${params.toString()}`);
    return res;
  }
);

// Create category service
export const createCategoryService = createAsyncThunk(
  "categoryServices/createCategoryService",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/category-services", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create category service"
      );
    }
  }
);

// Fetch category service by UUID
export const fetchCategoryServiceById = createAsyncThunk(
  "categoryServices/fetchCategoryServiceById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/category-services/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch category service details"
      );
    }
  }
);

// Update category service
export const updateCategoryService = createAsyncThunk(
  "categoryServices/updateCategoryService",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/dashboard/category-services/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update category service"
      );
    }
  }
);

// Delete category service
export const deleteCategoryService = createAsyncThunk(
  "categoryServices/deleteCategoryService",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/category-services/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete category service"
      );
    }
  }
);

// Enable category service
export const enableCategoryService = createAsyncThunk(
  "categoryServices/enableCategoryService",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/category-services/${uuid}/enable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to enable category service"
      );
    }
  }
);

// Disable category service
export const disableCategoryService = createAsyncThunk(
  "categoryServices/disableCategoryService",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/category-services/${uuid}/disable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to disable category service"
      );
    }
  }
);

// Toggle category service status
export const toggleCategoryServiceStatus = createAsyncThunk(
  "categoryServices/toggleCategoryServiceStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/category-services/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle category service status"
      );
    }
  }
);

const categoryServicesSlice = createSlice({
  name: "categoryServices",
  initialState: {
    categoryServices: [],
    pagination: null,
    categoryService: null,
    categoryServiceCreateStatus: "idle",
    categoryServiceUpdateStatus: "idle",
    categoryServiceDeleteStatus: "idle",
    categoryServiceFetchStatus: "idle",
    categoryServiceStatusChangeStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetCategoryServiceStatuses: (state) => {
      state.categoryServiceCreateStatus = "idle";
      state.categoryServiceUpdateStatus = "idle";
      state.categoryServiceDeleteStatus = "idle";
      state.categoryServiceStatusChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch category services
      .addCase(fetchCategoryServices.fulfilled, (state, action) => {
        state.categoryServices = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create category service
      .addCase(createCategoryService.pending, (state) => {
        state.categoryServiceCreateStatus = "loading";
      })
      .addCase(createCategoryService.fulfilled, (state, action) => {
        state.categoryServiceCreateStatus = "success";
        state.categoryService = action.payload.data;
      })
      .addCase(createCategoryService.rejected, (state) => {
        state.categoryServiceCreateStatus = "failed";
      })

      // Fetch category service by ID
      .addCase(fetchCategoryServiceById.pending, (state) => {
        state.categoryServiceFetchStatus = "loading";
      })
      .addCase(fetchCategoryServiceById.fulfilled, (state, action) => {
        state.categoryServiceFetchStatus = "success";
        state.categoryService = action.payload.data;
      })
      .addCase(fetchCategoryServiceById.rejected, (state) => {
        state.categoryServiceFetchStatus = "failed";
        state.categoryService = null;
      })

      // Update category service
      .addCase(updateCategoryService.pending, (state) => {
        state.categoryServiceUpdateStatus = "loading";
      })
      .addCase(updateCategoryService.fulfilled, (state, action) => {
        state.categoryServiceUpdateStatus = "success";
        const index = state.categoryServices.findIndex(
          (service) => service.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.categoryServices[index] = action.payload.data;
        }
        state.categoryService = action.payload.data;
      })
      .addCase(updateCategoryService.rejected, (state) => {
        state.categoryServiceUpdateStatus = "failed";
      })

      // Delete category service
      .addCase(deleteCategoryService.pending, (state) => {
        state.categoryServiceDeleteStatus = "loading";
      })
      .addCase(deleteCategoryService.fulfilled, (state, action) => {
        state.categoryServiceDeleteStatus = "success";
        state.categoryServices = state.categoryServices.filter(
          (service) => service.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteCategoryService.rejected, (state) => {
        state.categoryServiceDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleCategoryServiceStatus.pending, (state) => {
        state.categoryServiceStatusChangeStatus = "loading";
      })
      .addCase(toggleCategoryServiceStatus.fulfilled, (state, action) => {
        state.categoryServiceStatusChangeStatus = "success";
        const index = state.categoryServices.findIndex(
          (service) => service.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.categoryServices[index] = action.payload.data;
        }
      })
      .addCase(toggleCategoryServiceStatus.rejected, (state) => {
        state.categoryServiceStatusChangeStatus = "failed";
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetCategoryServiceStatuses } = categoryServicesSlice.actions;
export default categoryServicesSlice.reducer;
